import {
  IconDocument,
  IconHome,
  IconInference,
  IconJobAutomation,
  IconRegistries,
  IconRocketLaunch,
} from '@wandb/weave/components/Icon';
import React, {useContext} from 'react';

import {useIsAutomationHubEnabled} from '../../../components/Automations/hooks/useIsAutomationHubEnabled';
import {usePublicQueueAccess} from '../../../components/Launch/hooks';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from '../../../components/Launch/launchActive';
import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../../../components/Search/SearchNav/types';
import {envIsServerDeployment} from '../../../config';
import {
  trackGlobalRegistryClicked,
  trackProfileMenuClicked,
} from '../../../util/navigation';
import {
  useRampFlagInferenceCatalog,
  useRampFlagMarimoNotebooksWithLoading,
} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import * as urls from '../../../util/urls';
import {useRegistryAccess} from '../../../util/useModelsAccess';
import {useHomePageContext} from '../HomePageContextProvider';
import * as S from './HomePageSidebar.styles';
import {ProjectsList} from './ProjectsList';
import {SidebarLink} from './SidebarLink';
import {SidebarSectionHeader} from './SidebarSectionHeader';
import {TeamsList} from './TeamsList';
import {TrialCard} from './TrialCard';

// TODO(forge): Legacy global nav. Only renders in the non-Forge (standalone) entry point; dedicated deployments still use it. Remove entirely once dedicated is switched over to Forge shell mode. In shell mode the home page renders ForgeHomeSidebar instead.
export const HomePageSidebar = () => {
  const {
    homePageViewerDetailsQuery: {data: viewerData, refetch: refetchViewerData},
  } = useHomePageContext();

  const {hasRegistryAccess} = useRegistryAccess();

  const {isLaunchActive, error: isLaunchActiveError} =
    useAccountOrganizationOrPersonalEntityUsingLaunch();
  const {hasPublicQueueAccess} = usePublicQueueAccess();

  const {selectedAccount} = useContext(AccountSelectorContext);
  const {isAutomationHubEnabled} = useIsAutomationHubEnabled();
  const {isEnabled: notebooksEnabled} = useRampFlagMarimoNotebooksWithLoading();
  const isNotebooksNavEnabled = notebooksEnabled === true;
  const showCoreSection =
    viewerData?.username != null &&
    (hasRegistryAccess || isNotebooksNavEnabled);

  const isPersonalAccount =
    selectedAccount?.accountType === AccountType.Personal;
  const isNonServerDeploymentPersonalAccount =
    !envIsServerDeployment && isPersonalAccount;
  const isInferenceRampEnabled = useRampFlagInferenceCatalog(
    selectedAccount?.name ?? ''
  );
  const isCatalogLinkShown =
    isInferenceRampEnabled || isNonServerDeploymentPersonalAccount;

  if (isLaunchActiveError) {
    console.error('Error getting account or personal entity using launch');
  }

  return (
    <div
      data-test="home-page-sidebar"
      className={`[ul>*:p-0] flex h-full flex-col`}>
      <div className="mb-16">
        <SidebarLink
          dataTest="home-page-sidebar-header"
          icon={<IconHome />}
          labelText="Home"
          url={urls.homePage()}
        />
      </div>
      <ProjectsList />
      {showCoreSection && (
        <div className="mb-16">
          <SidebarSectionHeader headerText="Core" />
          {hasRegistryAccess && (
            <>
              <SidebarLink
                dataTest="global-registry"
                icon={<IconRegistries />}
                labelText="Registry"
                url={urls.registries()}
                isActive={urls.isOnRegistriesPage}
                onClick={() => {
                  trackGlobalRegistryClicked('Home sidebar');
                }}
              />
              {(isLaunchActive || hasPublicQueueAccess) && (
                <SidebarLink
                  icon={<IconRocketLaunch />}
                  labelText="Launch"
                  url={urls.launchOverview()}
                />
              )}
              {isCatalogLinkShown && (
                <SidebarLink
                  icon={<IconInference />}
                  labelText="Inference"
                  url={urls.inferenceModels()}
                />
              )}
              {isAutomationHubEnabled && (
                <SidebarLink
                  icon={<IconJobAutomation />}
                  labelText="Automations"
                  url={urls.homeAutomations()}
                />
              )}
            </>
          )}
          {isNotebooksNavEnabled && (
            <SidebarLink
              icon={<IconDocument />}
              labelText="Notebooks"
              url={urls.homeNotebooks()}
            />
          )}
        </div>
      )}
      {viewerData?.username && (
        <div className="mb-16">
          <SidebarSectionHeader headerText="Profile" />
          <SidebarLink
            key={viewerData.username}
            icon={
              <S.AvatarImage
                alt="Home nav profile avatar"
                src={viewerData.photoUrl}
              />
            }
            labelText={viewerData.username}
            url={urls.profilePage(viewerData.username)}
            onClick={() => {
              trackProfileMenuClicked('Home sidebar');
            }}
          />
        </div>
      )}
      {!isPersonalAccount && (
        <TeamsList
          name="Teams"
          data={viewerData?.teams ?? {edges: []}}
          userQueryRefetch={refetchViewerData}
        />
      )}
      {/* Forces Sticky Footer positioning */}
      <div className="flex flex-auto flex-col justify-end">
        <div className="sticky bottom-16">
          {!envIsServerDeployment && <TrialCard />}
        </div>
      </div>
    </div>
  );
};

export default HomePageSidebar; // displayName
